import { Link } from 'react-router-dom';
import { PATHS } from '@/content/site-content';
import { PATH_COLLAGE, PATH_IMAGES } from '@/content/images';
import { buttonClasses } from '@/components/ui/button-styles';
import { ArrowRightIcon, CheckIcon, UsersIcon } from '@/components/ui/Icons';

/**
 * Two audiences, side by side, sitting in a staggered photo collage.
 *
 * On wide screens the loose photographs interleave with the two choice cards
 * exactly as in the design; below `lg` the collage drops away and the two cards
 * stack, because a five-column stagger is unreadable on a phone.
 */
/** The three hand-drawn strokes the design puts beside the section title. */
function Sparks({ className = '' }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" focusable="false">
      <path
        d="M4 13 1 16M8 10 7 5.5M12 12l3.5-2.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

export function ChoosePathSection() {
  const [first, middle, last] = PATH_COLLAGE;

  return (
    <section id="categories" className="container-page scroll-mt-28 py-16 lg:py-20">
      <h2 className="text-center text-3xl font-extrabold sm:text-4xl">
        Choose Your Path
        <Sparks className="ml-1 inline-block size-6 align-top text-indigo-500" />
      </h2>

      <div className="mt-12 grid items-center gap-6 lg:grid-cols-[0.8fr_1fr_0.8fr_1fr_0.8fr]">
        {first && <CollagePhoto image={first} className="hidden lg:block" />}
        <PathCard path={PATHS[0]!} />
        {middle && <CollagePhoto image={middle} className="hidden lg:block" offset="lg:mt-16" />}
        <PathCard path={PATHS[1]!} />
        {last && <CollagePhoto image={last} className="hidden lg:block" offset="lg:mt-10" />}
      </div>
    </section>
  );
}

function CollagePhoto({
  image,
  className = '',
  offset = '',
}: {
  image: { src: string; alt: string; width: number; height: number };
  className?: string;
  offset?: string;
}) {
  return (
    <img
      src={image.src}
      alt=""
      width={image.width}
      height={image.height}
      loading="lazy"
      decoding="async"
      aria-hidden="true"
      className={`aspect-3/4 w-full rounded-2xl object-cover ${offset} ${className}`}
    />
  );
}

function PathCard({ path }: { path: (typeof PATHS)[number] }) {
  const image = PATH_IMAGES[path.key];

  return (
    <div className="flex flex-col">
      <div className="relative overflow-hidden rounded-2xl">
        <img
          src={image.src}
          alt={image.alt}
          width={image.width}
          height={image.height}
          loading="lazy"
          decoding="async"
          className="aspect-4/3 w-full object-cover"
        />
        <div className="absolute inset-x-0 bottom-0 flex items-center gap-3 bg-gradient-to-t from-black/70 to-transparent p-4 pt-10">
          <span
            aria-hidden="true"
            className="grid size-10 shrink-0 place-items-center rounded-full bg-brand-600 text-white"
          >
            <UsersIcon className="size-5" />
          </span>
          <p className="text-lg leading-tight font-extrabold text-white">
            {path.name}
            <span className="block text-xs font-medium text-white/75">{path.ages}</span>
          </p>
        </div>
      </div>

      <div className="mt-4 flex flex-1 flex-col rounded-2xl bg-accent-900 p-5 text-white">
        <ul className="space-y-2.5">
          {path.bullets.map((bullet) => (
            <li key={bullet} className="flex gap-2.5 text-sm">
              <CheckIcon className="mt-0.5 size-4 shrink-0 text-brand-600" strokeWidth={2.5} />
              {bullet}
            </li>
          ))}
        </ul>

        <Link to="/register" className={buttonClasses('onDark', 'sm', 'mt-5 w-full')}>
          {path.cta}
          <ArrowRightIcon className="size-4" />
        </Link>
      </div>
    </div>
  );
}
